import React, { useState } from 'react';
import { BaseButton, BaseModal } from '@components';
import { HintText } from '@components/input/styled.tsx';
import CloseIcon from '@components/modal/close-icon';
import { ALERT_STATUSES, AlertNotifications } from '@constants/alertNotifications.ts';
import { INTERNAL_SERVER_ERROR_STATUS_CODE } from '@constants/api.ts';
import { useAppDispatch } from '@hooks/reduxHooks.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import { ROUTES } from '@router/constants.ts';
import {
    SHARED_RESET_PASSWORD_MODAL_BACK_TO_LOGIN,
    SHARED_RESET_PASSWORD_MODAL_CANCEL,
    SHARED_RESET_PASSWORD_MODAL_CONFIRM,
    SHARED_RESET_PASSWORD_MODAL_CONFIRMED_TITLE,
    SHARED_RESET_PASSWORD_MODAL_DESCRIPTION,
    SHARED_RESET_PASSWORD_MODAL_RESET_REQUEST_CONFIRMED,
    SHARED_RESET_PASSWORD_MODAL_TITLE,
    SHARED_RESET_PASSWORD_MODAL_UNKNOWN_ERROR,
} from '@routes/auth/___shared___/reset-password-modal/constants.ts';
import { ButtonsWrapper, FooterWrapper } from '@routes/auth/___shared___/reset-password-modal/styled.tsx';
import { resetUserPassword } from '@routes/auth/register/model/register.slice.ts';
import { clearAlert, setAlert } from '@shared/model/alert.slice.ts';
import { renderAlertErrorDesc } from '@utils/general.tsx';
import { translateItem } from '@utils/translations.ts';

export type ResetPasswordModalProps = {
    open: boolean;
    email: string;
    onClose: () => void;
};

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ open, email, onClose }) => {
    const navigate = useCustomNavigate();

    const RESET_PASSWORD_MODAL_MSG = translateItem(SHARED_RESET_PASSWORD_MODAL_DESCRIPTION);
    const RESET_REQUEST_CONFIRMED_MSG = translateItem(SHARED_RESET_PASSWORD_MODAL_RESET_REQUEST_CONFIRMED);

    const dispatch = useAppDispatch();
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleClose = () => {
        setErrorMessage('');
        onClose();
    };

    const handleBackToLogin = () => {
        onClose();
        navigate(ROUTES.AUTH.LOGIN, { replace: true });
    };

    const handleConfirm = () => {
        setIsLoading(true);
        setErrorMessage('');
        dispatch(resetUserPassword({ email, callbackUrl: `/${ROUTES.AUTH.LOGIN}` }))
            .then(() => setIsConfirmed(true))
            .catch((err) => {
                if (err?.data && typeof err.data === 'string') {
                    setErrorMessage(err.data);
                } else {
                    setErrorMessage(translateItem(SHARED_RESET_PASSWORD_MODAL_UNKNOWN_ERROR));
                }

                if (err.status < INTERNAL_SERVER_ERROR_STATUS_CODE) {
                    dispatch(clearAlert()); //skip banner
                } else {
                    dispatch(
                        setAlert({
                            type: ALERT_STATUSES.ERROR,
                            message: AlertNotifications[ALERT_STATUSES.ERROR].message,
                            description: renderAlertErrorDesc(err.status, err.statusText),
                        }),
                    );
                }
            })
            .finally(() => setIsLoading(false));
    };

    const title = isConfirmed
        ? translateItem(SHARED_RESET_PASSWORD_MODAL_CONFIRMED_TITLE)
        : translateItem(SHARED_RESET_PASSWORD_MODAL_TITLE);
    const content = <div>{isConfirmed ? RESET_REQUEST_CONFIRMED_MSG : RESET_PASSWORD_MODAL_MSG}</div>;

    const footer = isConfirmed ? (
        <FooterWrapper>
            <BaseButton onClick={handleBackToLogin}>
                {translateItem(SHARED_RESET_PASSWORD_MODAL_BACK_TO_LOGIN)}
            </BaseButton>
        </FooterWrapper>
    ) : (
        <FooterWrapper>
            <ButtonsWrapper>
                <BaseButton loading={isLoading} onClick={handleConfirm}>
                    {translateItem(SHARED_RESET_PASSWORD_MODAL_CONFIRM)}
                </BaseButton>
                <BaseButton $customType="outline" onClick={handleClose}>
                    {translateItem(SHARED_RESET_PASSWORD_MODAL_CANCEL)}
                </BaseButton>
            </ButtonsWrapper>
            {!!errorMessage && <HintText className="has-error">{errorMessage}</HintText>}
        </FooterWrapper>
    );

    return (
        <BaseModal
            title={title}
            content={content}
            open={open}
            onClose={handleClose}
            footer={footer}
            closeIcon={<CloseIcon onClick={onClose} />}
        />
    );
};

export default ResetPasswordModal;
